import { useCallback, useEffect, useState } from "react";

import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from "@mui/material";

import { useAuth } from "../../authentication";
import { ProductCategoryTypeEnum } from "../../product-categories";
import { OutletProductSelect } from "../../products";
import { apiClient, StatusEnum, getErrorMessage } from "../../shared";
import { CustomDialogTitle, CustomInputField } from "../../ui";
import { useSnackbar } from "../../ui";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./add-sales-order-default-general-product-variant-form-constants";

export const AddSalesOrderDefaultGeneralProductVariantFormDialog = ({
  onClose,
  onSubmitted,
}) => {
  const { showSnackbar } = useSnackbar();
  const { outlet } = useAuth();
  const { control, watch, handleSubmit, setValue, reset } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const product = watch("product");
  const productVariant = watch("productVariant");
  const variantAttribute1Value = watch("variantAttribute1Value");
  const variantAttribute2Value = watch("variantAttribute2Value");
  const variantAttribute3Value = watch("variantAttribute3Value");
  const variantAttribute4Value = watch("variantAttribute4Value");

  const [loadingVariantAttributeValue, setLoadingVariantAttributeValue] =
    useState(false);
  const [productVariantAttributeValue, setProductVariantAttributeValue] =
    useState(null);
  const [loadingProductVariant, setLoadingProductVariant] = useState(false);

  const fetchVariantAttributeValue = useCallback(async () => {
    setProductVariantAttributeValue(null);
    setLoadingVariantAttributeValue(true);
    try {
      const {
        data: { data },
      } = await apiClient.get(
        `products/${product.id}/product-variant-attribute-values`
      );
      setProductVariantAttributeValue(data);
    } finally {
      setLoadingVariantAttributeValue(false);
    }
  }, [product]);

  const fetchProductVariant = useCallback(async () => {
    setValue("productVariant", null);
    setLoadingProductVariant(true);
    try {
      const {
        data: { data },
      } = await apiClient.get(`products/${product.id}/product-variants`, {
        params: {
          variantAttribute1Value,
          variantAttribute2Value,
          variantAttribute3Value,
          variantAttribute4Value,
        },
      });
      if (data.length > 0) setValue("productVariant", data[0]);
    } finally {
      setLoadingProductVariant(false);
    }
  }, [
    product,
    setValue,
    variantAttribute1Value,
    variantAttribute2Value,
    variantAttribute3Value,
    variantAttribute4Value,
  ]);

  useEffect(() => {
    if (product) {
      fetchVariantAttributeValue();
    }
  }, [fetchVariantAttributeValue, product]);

  const handleProductChange = (newValue) => {
    setProductVariantAttributeValue(null);
    reset({
      ...DEFAULT_VALUES,
      product: newValue,
      variantAttribute1Value: null,
      variantAttribute2Value: null,
      variantAttribute3Value: null,
      variantAttribute4Value: null,
      productVariant: null,
    });
  };

  useEffect(() => {
    if (productVariantAttributeValue) {
      if (
        productVariantAttributeValue.variantAttribute1 &&
        !variantAttribute1Value
      ) {
        return;
      }
      if (
        productVariantAttributeValue.variantAttribute2 &&
        !variantAttribute2Value
      ) {
        return;
      }
      if (
        productVariantAttributeValue.variantAttribute3 &&
        !variantAttribute3Value
      ) {
        return;
      }
      if (
        productVariantAttributeValue.variantAttribute4 &&
        !variantAttribute4Value
      ) {
        return;
      }

      fetchProductVariant();
    }
  }, [
    fetchProductVariant,
    productVariantAttributeValue,
    variantAttribute1Value,
    variantAttribute2Value,
    variantAttribute3Value,
    variantAttribute4Value,
  ]);

  const onSubmit = async ({ productVariant }) => {
    try {
      await apiClient.post("sales-order-default-general-product-variants", {
        productVariantId: productVariant.id,
      });
      onSubmitted();
      onClose();
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal menambahkan produk default"));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      component="form"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <CustomDialogTitle
        variant="h5"
        title="Tambah Default Produk Umum"
        onClose={onClose}
      />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Controller
              control={control}
              name="product"
              render={({ field, fieldState }) => (
                <OutletProductSelect
                  outletId={outlet?.id}
                  productCategoryType={ProductCategoryTypeEnum.NON_LENS}
                  value={field.value}
                  onChange={handleProductChange}
                  TextFieldProps={{
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )}
            />
          </Grid>
          {loadingVariantAttributeValue && (
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="body2"
                sx={{
                  fontStyle: "italic",
                  color: "gray",
                }}
              >
                Memuat atribut varian...
              </Typography>
            </Grid>
          )}
          {productVariantAttributeValue?.variantAttribute1 && (
            <Grid size={{ xs: 6 }}>
              <Controller
                control={control}
                name="variantAttribute1Value"
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    value={field.value}
                    onChange={(_, newValue) => {
                      setValue("variantAttribute1Value", newValue);
                    }}
                    disableClearable={true}
                    options={
                      productVariantAttributeValue.variantAttribute1Values
                    }
                    renderInput={(params) => (
                      <CustomInputField
                        {...params}
                        required={true}
                        label={
                          productVariantAttributeValue.variantAttribute1.name
                        }
                        InputProps={{
                          ...params.slotProps.input,
                          placeholder: "Pilih",
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {productVariantAttributeValue?.variantAttribute2 && (
            <Grid size={{ xs: 6 }}>
              <Controller
                control={control}
                name="variantAttribute2Value"
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    value={field.value}
                    onChange={(_, newValue) => {
                      setValue("variantAttribute2Value", newValue);
                    }}
                    disableClearable={true}
                    options={
                      productVariantAttributeValue.variantAttribute2Values
                    }
                    renderInput={(params) => (
                      <CustomInputField
                        {...params}
                        required={true}
                        label={
                          productVariantAttributeValue.variantAttribute2.name
                        }
                        InputProps={{
                          ...params.slotProps.input,
                          placeholder: "Pilih",
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {productVariantAttributeValue?.variantAttribute3 && (
            <Grid size={{ xs: 6 }}>
              <Controller
                control={control}
                name="variantAttribute3Value"
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    value={field.value}
                    onChange={(_, newValue) => {
                      setValue("variantAttribute3Value", newValue);
                    }}
                    disableClearable={true}
                    options={
                      productVariantAttributeValue.variantAttribute3Values
                    }
                    renderInput={(params) => (
                      <CustomInputField
                        {...params}
                        required={true}
                        label={
                          productVariantAttributeValue.variantAttribute3.name
                        }
                        InputProps={{
                          ...params.slotProps.input,
                          placeholder: "Pilih",
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {productVariantAttributeValue?.variantAttribute4 && (
            <Grid size={{ xs: 6 }}>
              <Controller
                control={control}
                name="variantAttribute4Value"
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    value={field.value}
                    onChange={(_, newValue) => {
                      setValue("variantAttribute4Value", newValue);
                    }}
                    disableClearable={true}
                    options={
                      productVariantAttributeValue.variantAttribute4Values
                    }
                    renderInput={(params) => (
                      <CustomInputField
                        {...params}
                        required={true}
                        label={
                          productVariantAttributeValue.variantAttribute4.name
                        }
                        InputProps={{
                          ...params.slotProps.input,
                          placeholder: "Pilih",
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {loadingProductVariant && (
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="body2"
                sx={{
                  fontStyle: "italic",
                  color: "gray",
                }}
              >
                Memuat informasi produk varian...
              </Typography>
            </Grid>
          )}
          {productVariant?.status === StatusEnum.INACTIVE && (
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="body2"
                color="error"
                sx={{
                  fontStyle: "italic",
                }}
              >
                Barang ini tidak aktif
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      {productVariant?.status === StatusEnum.ACTIVE && (
        <DialogActions>
          <Button type="submit" variant="contained">
            Simpan
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AddSalesOrderDefaultGeneralProductVariantFormDialog;

AddSalesOrderDefaultGeneralProductVariantFormDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func.isRequired,
};
